import { HotelReservedCardType } from "@website/accommodation-ui/hotel-reserve-rooms-layout";
import { addEventToDataLayer } from "@website/tracking/tracking.gtm";
import {
  DATA_LAYER_EVENTS,
  USER_TRACKING_EVENTS,
  WEBENGAGE_EVENTS
} from "@website/tracking/tracking.types";
import { addEventToWebengage } from "@website/tracking/tracking.webengage";
import { addEventToTracking } from "@website/tracking/user";
import { OnVisibleCommentType } from "@website/widget/reserve-comment";
import debounce from "lodash-es/debounce";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import {
  HotelCreateBookPropertiesTypes,
  HotelDomesticHotelDataType
} from "./index.types";

export const useHotelPageTracking = (
  hotelData?: HotelDomesticHotelDataType,
  showRoomDetail?: boolean,
  hotelRoomsData?: Array<HotelReservedCardType>,
  hotelRoomsLoading?: boolean
) => {
  const router = useRouter();

  useEffect(() => {
    if (!hotelData) return;

    addEventToWebengage(WEBENGAGE_EVENTS.HOTEL_VISIT, {
      "hotel name": hotelData.title,
      "hotel ID": hotelData.id,
      "hotel star": hotelData.stars,
      "city name": hotelData.city_title,
      "check in date": toString(router.query.date_from),
      "check out date": toString(router.query.date_to),
      "page url": window.location.href.split("?"[0])
    });
  }, [hotelData, router]);

  useEffect(() => {
    if (!hotelData || hotelRoomsLoading) return;

    const query = new URLSearchParams(window.location.search);

    const debouncedTrackEvent = debounce(() => {
      addEventToTracking(USER_TRACKING_EVENTS.PAGE_VISIT_PROPERTY, {
        product: "dom-hotel",
        city: hotelData.city_title,
        hotel: hotelData.title,
        date_from: query.get("date_from"),
        date_to: query.get("date_to"),
        city_id: hotelData.city_id,
        hotel_id: hotelData.id,
        available_rooms_id: hotelRoomsData
          ?.filter((room) => room.availableRooms >= 1)
          .map((room) => room.id as number)
      });
    }, 500);

    debouncedTrackEvent();

    return () => {
      debouncedTrackEvent.cancel();
    };
  }, [hotelData, hotelRoomsData, hotelRoomsLoading]);

  const handleTrackHotelPage = useCallback(
    (reservationData?: HotelCreateBookPropertiesTypes) => {
      addEventToDataLayer(DATA_LAYER_EVENTS.ADD_TO_CART, {
        items: [
          {
            item_name: hotelData.title,
            item_category: hotelData.city_title,
            item_category2: hotelData.accommodation_type,
            item_category3: hotelData?.stars,
            item_category4: reservationData.title,
            item_id: hotelData.id,
            guest_count: reservationData.adults,
            price: reservationData.price,
            discount: reservationData.price - reservationData.priceOff,
            item_brand: "IRR",
            item_night_stay: reservationData?.nightCount,
            phone: "", // To Do
            quantity: reservationData?.roomCount
          }
        ]
      });

      addEventToWebengage(WEBENGAGE_EVENTS.HOTEL_RESERVATION, {
        "hotel name": hotelData.title,
        "hotel ID": hotelData.id,
        "hotel star": hotelData.stars,
        "city name": hotelData.city_title,
        "check in date": toString(router.query.date_from),
        "check out date": toString(router.query.date_to),
        "page url": window.location.href,
        pageUrl: window.location.href.split("?"[0]),
        cityId: hotelData.city_id,
        roomType: reservationData.title,
        roomNight: reservationData?.nightCount,
        price: reservationData.price
      });

      addEventToTracking(USER_TRACKING_EVENTS.ROOM_RESERVE_BUTTON_CLICKED, {
        product: "dom-hotel",
        date_from: toString(router.query.date_from),
        date_to: toString(router.query.date_to),
        city: hotelData.city_title,
        city_id: hotelData.city_id,
        hotel: hotelData.title,
        hotel_id: hotelData.id,
        price_engine_rules: [
          {
            id: reservationData?.ruleId,
            title: ""
          }
        ],
        rooms: [
          {
            id: reservationData.id,
            type_id: reservationData.roomTypeId,
            title: reservationData.title,
            available_rooms: reservationData.availableRooms,
            base_price: reservationData.basePrice,
            local_price: reservationData.price,
            local_price_off: reservationData.priceOff,
            original_sell_price: reservationData.originalSellPrice,
            child_price: reservationData.childPrice,
            extra_bed: reservationData.extraBed,
            breakfast_included: reservationData.hasBreakfast,
            adult: reservationData.adults,
            bed_price: 0,
            has_foreign_guest: false,
            child: 0,
            child_age: 0,
            infant_age: 0,
            adult_reserved: 0,
            child_reserved: 0
          }
        ],
        is_available: reservationData.availableRooms > 0
      });
    },
    [hotelData, router.query]
  );

  const handleVisibleRoomCard = (inView, room) => {
    if (!inView || showRoomDetail) return;

    addEventToTracking(USER_TRACKING_EVENTS.ROOM_RESERVE_CARD_VIEWED, {
      product: "dom-hotel",
      date_from: toString(router.query.date_from),
      date_to: toString(router.query.date_to),
      city: hotelData.city_title,
      city_id: hotelData.city_id,
      hotel: hotelData.title,
      hotel_id: hotelData.id,
      price_engine_rules: [
        {
          id: room?.ruleId,
          title: ""
        }
      ],
      rooms: [
        {
          id: room.id,
          type_id: room.roomTypeId,
          title: room.title,
          available_rooms: room.availableRooms,
          base_price: room.basePrice,
          local_price: room.price,
          local_price_off: room.priceOff,
          original_sell_price: room.originalSellPrice,
          child_price: room.childPrice,
          extra_bed: room.extraBed,
          breakfast_included: room.hasBreakfast,
          adult: room.adults,
          bed_price: 0,
          has_foreign_guest: false,
          child: 0,
          child_age: 0,
          infant_age: 0,
          adult_reserved: 0,
          child_reserved: 0
        }
      ],
      is_available: room.availableRooms > 0
    });
  };

  const handleTrackShowMoreDetail = (availableRooms, selectedRoom) => {
    addEventToTracking(USER_TRACKING_EVENTS.ROOM_RESERVE_DETAIL_VIEWED, {
      product: "dom-hotel",
      date_from: toString(router.query.date_from),
      date_to: toString(router.query.date_to),
      city: hotelData.city_title,
      city_id: hotelData.city_id,
      hotel: hotelData.title,
      hotel_id: hotelData.id,
      rooms: [
        {
          id: selectedRoom.id,
          title: selectedRoom.title,
          available_rooms: selectedRoom.availableRooms,
          base_price: selectedRoom.basePrice,
          local_price: selectedRoom.price,
          local_price_off: selectedRoom.priceOff,
          original_sell_price: selectedRoom.originalSellPrice,
          child_price: selectedRoom.childPrice,
          extra_bed: selectedRoom.extraBed,
          breakfast_included: selectedRoom.hasBreakfast,
          adult: selectedRoom.adults,
          type_id: 0,
          bed_price: 0,
          has_foreign_guest: false,
          child: 0,
          child_age: 0,
          infant_age: 0,
          adult_reserved: 0,
          child_reserved: 0
        }
      ],
      is_available: availableRooms > 0
    });
  };

  const handleTrackShowUnavailableRooms = () => {
    addEventToTracking(
      USER_TRACKING_EVENTS.ROOM_RESERVE_NON_AVAILABLE_BUTTON_CLICKED,
      {
        product: "dom-hotel",
        date_from: toString(router.query.date_from),
        date_to: toString(router.query.date_to),
        city: hotelData.city_title,
        city_id: hotelData.city_id,
        hotel: hotelData.title,
        hotel_id: hotelData.id
      }
    );
  };

  const handleTrackVisibleComment = useCallback(
    (data: OnVisibleCommentType) => {
      addEventToTracking(USER_TRACKING_EVENTS.REVIEW_VIEWED, {
        product: "dom-hotel",
        submit_date: data.createdAt,
        city_id: hotelData.city_id,
        city: hotelData.city_title,
        hotel_id: hotelData.id,
        rooms: [
          {
            id: data.roomId,
            type_id: 0,
            title: "",
            available_rooms: 0,
            base_price: 0,
            local_price: 0,
            local_price_off: 0,
            original_sell_price: 0,
            bed_price: 0,
            child_price: 0,
            has_foreign_guest: false,
            extra_bed: 0,
            breakfast_included: false,
            adult: 0,
            child: 0,
            child_age: 0,
            infant_age: 0,
            adult_reserved: 0,
            child_reserved: 0
          }
        ],
        comment_entered: Boolean(data.comment),
        photo_included: Boolean(data.pictures),
        review_id: data.id,
        usefulness_count: 0
      });
    },
    [hotelData]
  );

  return {
    handleTrackHotelPage,
    handleVisibleRoomCard,
    handleTrackShowMoreDetail,
    handleTrackShowUnavailableRooms,
    handleTrackVisibleComment
  };
};
